import {
  useMemo,
  useCallback,
  useRef,
  useContext,
  useState,
  useEffect,
} from "react";
import {
  getCoreRowModel,
  Updater,
  RowSelectionState,
  SortingState,
} from "@tanstack/react-table";
import { TABLE_SIZES } from "@adsk/alloy-react-table";
import type { TableSize } from "@adsk/alloy-react-table/es/types";
import { Issue } from "@adsk/acc-issues-types";
import {
  useTableColumnDefs,
  useGetInitialColumnVisibility,
  useColumnKeysInDefaultOrder,
} from "./useTableColumnDefs";
import { usePersistedState } from "./useTableLocalStorage";
import { LocalStorageKeys } from "@adsk/acc-issues-core/state/localStorage";
import { DeletedIssuesSpaceContext } from "@adsk/acc-issues-core/providers/DeletedIssuesSpaceContext";
import {
  ISSUE_COLUMN_KEYS,
  ACTIONS_COLUMN,
  SELECT_COLUMN,
  isCustomFieldColumn,
} from "./columnKeys";

const isDeletionColumn = (id: string) =>
  id === ISSUE_COLUMN_KEYS.DELETED_BY || id === ISSUE_COLUMN_KEYS.DELETED_ON;

const insertNewColumns = (
  columnOrder: string[],
  currentColumnIds: Set<string>
): string[] | null => {
  const columnOrderSet = new Set(columnOrder);
  const newIds = [...currentColumnIds].filter(
    id =>
      !columnOrderSet.has(id) &&
      !isDeletionColumn(id) &&
      id !== SELECT_COLUMN &&
      id !== ACTIONS_COLUMN
  );
  if (!newIds.length) return null;
  const actionsIdx = columnOrder.indexOf(ACTIONS_COLUMN);
  const insertAt = actionsIdx === -1 ? columnOrder.length : actionsIdx;
  return [
    ...columnOrder.slice(0, insertAt),
    ...newIds,
    ...columnOrder.slice(insertAt),
  ];
};

interface UseTableConfigurationProps {
  data: Issue[];
  isLoading: boolean;
  externalSorting: SortingState;
  checkedRowIds: Record<string, boolean>;
  handleSortingChange: (updater: Updater<SortingState>) => void;
  handleRowCheckboxChange: (updater: Updater<RowSelectionState>) => void;
  handleExport: (issue: Issue) => void;
}

export const useTableConfiguration = ({
  data,
  isLoading,
  externalSorting,
  checkedRowIds,
  handleSortingChange,
  handleRowCheckboxChange,
  handleExport,
}: UseTableConfigurationProps) => {
  const initialColumnVisibility = useGetInitialColumnVisibility();
  const defaultColumnOrder = useColumnKeysInDefaultOrder();

  const [columnVisibility, setColumnVisibility] = usePersistedState<
    Record<string, boolean>
  >(
    LocalStorageKeys.ISSUE_LOG_COLUMN_SETTINGS,
    "columnVisibility",
    initialColumnVisibility
  );

  const [columnOrder, setColumnOrder] = usePersistedState<string[]>(
    LocalStorageKeys.ISSUE_LOG_COLUMN_SETTINGS,
    "columnOrder",
    []
  );

  const [tableSize, setTableSize] = usePersistedState<TableSize>(
    LocalStorageKeys.ISSUE_LOG_COLUMN_SETTINGS,
    "tableSize",
    TABLE_SIZES.MEDIUM
  );

  const [columnSizing, setColumnSizing] = usePersistedState<
    Record<string, number>
  >(LocalStorageKeys.ISSUE_LOG_COLUMN_SETTINGS, "columnSizing", {});

  const containerRef = useRef<HTMLDivElement>(null);

  const isDeletedSpaceActive = useContext(DeletedIssuesSpaceContext);

  const [hasAdjustedDeletedColumns, setHasAdjustedDeletedColumns] =
    useState(false);

  useEffect(() => {
    if (!isDeletedSpaceActive) {
      setHasAdjustedDeletedColumns(false);
      return;
    }

    if (hasAdjustedDeletedColumns || !columnOrder?.length) {
      return;
    }

    const columnOrderExcludingDeletionFields = columnOrder.filter(
      id => !isDeletionColumn(id)
    );

    const deletedByIndex = defaultColumnOrder.indexOf(
      ISSUE_COLUMN_KEYS.DELETED_BY
    );

    const columnsBeforeDeleted: string[] =
      deletedByIndex === -1 ? [] : defaultColumnOrder.slice(0, deletedByIndex);

    const columnsBeforeDeletedSet = new Set(columnsBeforeDeleted);

    const insertionIndex =
      deletedByIndex === -1
        ? columnOrderExcludingDeletionFields.length
        : columnOrderExcludingDeletionFields.reduce(
            (pos, id, i) => (columnsBeforeDeletedSet.has(id) ? i + 1 : pos),
            0
          );

    const newOrder = [
      ...columnOrderExcludingDeletionFields.slice(0, insertionIndex),
      ISSUE_COLUMN_KEYS.DELETED_BY,
      ISSUE_COLUMN_KEYS.DELETED_ON,
      ...columnOrderExcludingDeletionFields.slice(insertionIndex),
    ];

    setColumnOrder(newOrder);
    setColumnVisibility(prev => ({
      ...prev,
      [ISSUE_COLUMN_KEYS.DELETED_BY]: true,
      [ISSUE_COLUMN_KEYS.DELETED_ON]: true,
    }));

    setHasAdjustedDeletedColumns(true);
  }, [
    isDeletedSpaceActive,
    hasAdjustedDeletedColumns,
    columnOrder,
    defaultColumnOrder,
    setColumnOrder,
    setColumnVisibility,
  ]);

  const handleColumnVisibilityChange = useCallback(
    (updater: Updater<Record<string, boolean>>) => {
      setColumnVisibility(updater);
    },
    [setColumnVisibility]
  );

  const handleColumnOrderChange = useCallback(
    (updater: Updater<string[]>) => {
      setColumnOrder(updater);
    },
    [setColumnOrder]
  );

  const columns = useTableColumnDefs();

  const currentColumnIds = useMemo(
    () =>
      new Set(
        columns.map(col => col.id).filter((id): id is string => Boolean(id))
      ),
    [columns]
  );

  const currentColumnIdsRef = useRef(currentColumnIds);
  useEffect(() => {
    currentColumnIdsRef.current = currentColumnIds;
  }, [currentColumnIds]);

  const columnOrderWithNewColumns = useMemo(() => {
    if (!columnOrder || columnOrder.length === 0) return columnOrder;
    return insertNewColumns(columnOrder, currentColumnIds) ?? columnOrder;
  }, [columnOrder, currentColumnIds]);

  useEffect(() => {
    if (columnOrderWithNewColumns !== columnOrder) {
      setColumnOrder((prev: string[]) => {
        if (!prev.length) return prev;
        return insertNewColumns(prev, currentColumnIdsRef.current) ?? prev;
      });
    }
  }, [columnOrderWithNewColumns, columnOrder, setColumnOrder]);

  // Custom field columns should be hidden by default when they first appear
  // (e.g. a new custom field was added to a type).
  const columnVisibilityWithDefaults = useMemo(() => {
    const hiddenEntries: Record<string, boolean> = {};
    for (const id of currentColumnIds) {
      if (isCustomFieldColumn(id) && columnVisibility[id] === undefined) {
        hiddenEntries[id] = false;
      }
    }
    if (Object.keys(hiddenEntries).length === 0) return columnVisibility;
    return { ...columnVisibility, ...hiddenEntries };
  }, [columnVisibility, currentColumnIds]);

  useEffect(() => {
    if (columnVisibilityWithDefaults !== columnVisibility) {
      setColumnVisibility((prev: Record<string, boolean>) => {
        const entries: Record<string, boolean> = {};
        for (const id of currentColumnIdsRef.current) {
          if (isCustomFieldColumn(id) && prev[id] === undefined) {
            entries[id] = false;
          }
        }
        return Object.keys(entries).length === 0
          ? prev
          : { ...prev, ...entries };
      });
    }
  }, [
    columnVisibilityWithDefaults,
    columnVisibility,
    currentColumnIds,
    setColumnVisibility,
  ]);

  const coreRowModel = useMemo(() => getCoreRowModel(), []);

  const tableData = useMemo(() => (isLoading ? [] : data), [isLoading, data]);

  const tableMeta = useMemo(
    () => ({
      tableSize,
      setTableSize,
      onExport: handleExport,
      container: containerRef,
    }),
    [tableSize, setTableSize, handleExport, containerRef]
  );

  const tableState = useMemo(
    () => ({
      sorting: externalSorting,
      rowSelection: checkedRowIds,
      columnVisibility: columnVisibilityWithDefaults,
      columnOrder: columnOrderWithNewColumns,
      columnSizing,
    }),
    [
      externalSorting,
      checkedRowIds,
      columnVisibilityWithDefaults,
      columnOrderWithNewColumns,
      columnSizing,
    ]
  );

  const getRowId = useCallback((row: Issue) => row.id, []);

  const tableConfig = useMemo(
    () => ({
      columns,
      data: tableData,
      getCoreRowModel: coreRowModel,
      enableSorting: true,
      enableMultiSort: false,
      enableSortingRemoval: false,
      manualSorting: true,
      onSortingChange: handleSortingChange,
      enableRowSelection: true,
      enableMultiRowSelection: true,
      onRowSelectionChange: handleRowCheckboxChange,
      enableColumnResizing: true,
      columnResizeMode: "onChange" as const,
      onColumnSizingChange: setColumnSizing,
      enableHiding: true,
      onColumnVisibilityChange: handleColumnVisibilityChange,
      onColumnOrderChange: handleColumnOrderChange,
      getRowId,
      state: tableState,
      meta: tableMeta,
    }),
    [
      columns,
      tableData,
      coreRowModel,
      handleSortingChange,
      handleRowCheckboxChange,
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      setColumnSizing,
      getRowId,
      tableState,
      tableMeta,
    ]
  );

  return {
    coreRowModel,
    tableData,
    tableMeta,
    tableState,
    tableConfig,
    columnVisibility: columnVisibilityWithDefaults,
    columnOrder: columnOrderWithNewColumns,
    columnSizing,
    tableSize,
    containerRef,
    setColumnSizing,
    setTableSize,
  };
};
